import { Box, Button, Heading, HStack, Pressable, Text, VStack } from 'native-base';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import FixedBottom from 'app/components/FixedBottom';
import Screen from 'app/components/Screen';
import useDestinations from 'app/hooks/useDestinations/useDestinations';
import useGuest from 'app/hooks/useGuest';
import useMetaData from 'app/hooks/useMetaData';
import useProduct from 'app/hooks/useProduct';
import useStore from 'app/hooks/useStore';
import { PurchasesStackParamList } from 'app/navigation/PurchasesStackNavigator';
import { RootScreenProps } from 'app/navigation/RootNavigator';
import { useI18n } from 'app/providers/I18nProvider';
import DestinationCard from 'app/screens/DestinationsScreen/DestinationCard/DestinationCard';
import { TicketClass } from 'app/services/GuestCenterService.types';
import { getTextKey } from 'app/services/I18n';
import { isAlacarte } from 'app/services/ProductLineupHelper';
import { date } from 'app/services/ReservationHelper';
import { tID } from 'app/services/TestHelper';
import { unslugify } from 'app/utils/string';
import { areAllPassesExpired } from 'app/utils/time';

export type PurchaseOrderDetailsScreenProps = RootScreenProps<
  PurchasesStackParamList,
  'PurchaseOrderDetails'
>;

function noop() {}

function OrderDetailsField({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Heading fontSize="sm">{title}</Heading>
      <Text>{children}</Text>
    </Box>
  );
}

export default function PurchaseOrderDetailsScreen({
  navigation,
  route,
}: PurchaseOrderDetailsScreenProps) {
  const { t } = useI18n();
  const { allEntitlements } = useGuest();
  const { setEntitlementKey, setProduct } = useStore((s) => ({
    setEntitlementKey: s.setEntitlementKey,
    setProduct: s.setProduct,
  }));
  const { data: destinations = [] } = useDestinations();
  const [showMore, setShowMore] = useState(false);

  const entitlement = useMemo(
    () => allEntitlements.find((ent) => ent.entitlementKey === route.params.entitlementKey),
    [allEntitlements, route.params.entitlementKey]
  );

  useMetaData('Purchases');

  const product = destinations.find((d) => d.key === entitlement?.productContentKey);
  const productContentKey = entitlement?.productContentKey ?? '';
  const { productContent } = useProduct({
    enabled: !!productContentKey,
    productContentKey,
  });
  const entitlementName = entitlement?.name || unslugify(entitlement?.productContentKey ?? '');
  const isALaCarte = entitlement ? isAlacarte(entitlement.productLineup) : false;
  const travelParty = entitlement?.travelParty;
  const showOrderNumber = !!travelParty?.orderNumber;
  const showExpiration = !!travelParty && !isALaCarte && showOrderNumber;
  const showValidity =
    !!entitlement && !isALaCarte && entitlement.productLineup.usageWindowDays > 0;
  const canExpand = Boolean(
    showExpiration ||
      showValidity ||
      (!!entitlement && !isALaCarte && entitlement.passes.length > 0)
  );
  const isPastTrip = entitlement ? areAllPassesExpired(entitlement) : true;
  const ticketsAndReservationsBody =
    productContent?.reservation?.texts.find(
      (text) => getTextKey(text) === 'app_purchases_tickets_and_reservations_body'
    )?.value ??
    t('app_purchases_tickets_and_reservations_body', {
      product: entitlementName,
    });

  const handleManageTrip = useCallback(() => {
    if (!entitlement) return;

    setEntitlementKey(entitlement.entitlementKey);
    setProduct(entitlement.productContentKey);
    navigation.replace('Tabs', {
      screen: 'ProductStack',
      params: {
        screen: 'Product',
        params: {
          productId: entitlement.productContentKey,
          returnTo: 'PurchaseOrderDetails',
          entitlementKey: entitlement.entitlementKey,
        },
      },
    });
  }, [entitlement, navigation, setEntitlementKey, setProduct]);

  useEffect(() => {
    if (!entitlement) {
      navigation.goBack();
    }
  }, [entitlement, navigation]);

  if (!entitlement) {
    return null;
  }

  return (
    <Screen
      after={
        !isPastTrip ? (
          <FixedBottom bg="white" borderTopColor="coolGray.200" borderTopWidth="1" padded py="4">
            <Button testID={tID('ManageTripButton')} w="full" onPress={handleManageTrip}>
              {t('app_purchases_manage_trip')}
            </Button>
          </FixedBottom>
        ) : undefined
      }
      testID={tID('PurchaseOrderDetailsScreen')}
    >
      <VStack pb="24" pt="2" space="8">
        <VStack space="4">
          <VStack alignItems="flex-start" space="4">
            <Heading fontSize="lg">{entitlementName}</Heading>

            <HStack alignItems="flex-start" justifyContent="space-between" space="4" w="full">
              <VStack flex="1" space="4">
                {showOrderNumber && (
                  <OrderDetailsField title={t('app_order_num_title')}>
                    {travelParty?.orderNumber}
                  </OrderDetailsField>
                )}

                <OrderDetailsField title={t('app_group_title')}>
                  {t('app_ticket_class_summary', {
                    adult: entitlement.quantity[TicketClass.Adult],
                    child: entitlement.quantity[TicketClass.Child],
                  })}
                </OrderDetailsField>
              </VStack>

              <Box h="88px" overflow="hidden" rounded="4px" w="88px">
                {product ? (
                  <DestinationCard
                    product={product}
                    showLabel={false}
                    spotSize={24}
                    onPress={noop}
                  />
                ) : null}
              </Box>
            </HStack>
          </VStack>

          {canExpand && (
            <>
              {showMore && (
                <VStack space="4">
                  {showExpiration && (
                    <OrderDetailsField title={t('app_expiration_title')}>
                      {t('app_expiration_detail', {
                        date: date(travelParty?.firstUsageBy).format('MMM D, YYYY'),
                      })}
                    </OrderDetailsField>
                  )}

                  {showValidity && (
                    <OrderDetailsField title={t('app_validity_title')}>
                      {t('app_validity_detail', {
                        days: entitlement.productLineup.usageWindowDays.toString(),
                      })}
                    </OrderDetailsField>
                  )}

                  {!isALaCarte &&
                    entitlement.passes.map((pass) => (
                      <Box key={pass.barcode}>
                        <Text color="gray.500" numberOfLines={1}>
                          {pass.name ? `${pass.name} (${pass.ticketClass})` : pass.ticketClass}
                        </Text>
                        <Text color="gray.500">{pass.barcode}</Text>
                      </Box>
                    ))}
                </VStack>
              )}

              <Pressable
                testID={tID('OrderDetailsToggle')}
                onPress={() => setShowMore((current) => !current)}
              >
                <Text underline>
                  {showMore ? t('app_purchases_show_less') : t('app_purchases_show_more')}
                </Text>
              </Pressable>
            </>
          )}

          {!isPastTrip && (
            <VStack pt="6" space="3">
              <Heading>{t('app_purchases_tickets_and_reservations_title')}</Heading>
              <Text>{ticketsAndReservationsBody}</Text>
            </VStack>
          )}
        </VStack>
      </VStack>
    </Screen>
  );
}
